import React, { useState, useEffect, useRef } from 'react';
import { cva } from 'class-variance-authority';
import styles from './Statistic.module.css';

const statistic = cva(styles.base);

interface StatisticProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'prefix'> {
  value: number | string;
  title?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  precision?: number;
  formatter?: (value: number | string) => React.ReactNode;
  valueStyle?: React.CSSProperties;
}

/**
 * Statistic — key metric display.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | number \| string | — | Display value |
 * | title | ReactNode | — | Label above value |
 * | prefix | ReactNode | — | Before value |
 * | suffix | ReactNode | — | After value |
 * | precision | number | — | Decimal places |
 * | formatter | (value) => ReactNode | — | Custom formatter |
 * | valueStyle | CSSProperties | — | Value styles |
 *
 * Sub: Statistic.Countdown
 *
 * @example
 * <Statistic title="Total Thoughts" value={1128} />
 * <Statistic title="Revenue" value={93012} prefix="$" precision={2} />
 */
export function Statistic({
  value,
  title,
  prefix,
  suffix,
  precision,
  formatter,
  valueStyle,
  className,
  style,
  ...props
}: StatisticProps) {
  let displayValue: React.ReactNode;

  if (formatter) {
    displayValue = formatter(value);
  } else if (typeof value === 'number' && precision !== undefined) {
    displayValue = value.toFixed(precision);
  } else if (typeof value === 'number') {
    displayValue = value.toLocaleString();
  } else {
    displayValue = value;
  }

  return (
    <div className={statistic({ className })} style={style} {...props}>
      {title && (
        <div className={styles.title}>
          {title}
        </div>
      )}
      <div
        className={styles.valueRow}
        style={valueStyle}
      >
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <span>{displayValue}</span>
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
    </div>
  );
}

// ── Statistic.Countdown ──────────────────────────────────────────────────────

interface CountdownProps extends Omit<StatisticProps, 'value'> {
  value: number; // Target timestamp (ms)
  format?: string; // 'HH:mm:ss' or 'DD:HH:mm:ss'
  onFinish?: () => void;
}

function formatCountdown(diff: number, format: string): string {
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  return format
    .replace('DD', String(d).padStart(2, '0'))
    .replace('HH', String(h).padStart(2, '0'))
    .replace('mm', String(m).padStart(2, '0'))
    .replace('ss', String(s).padStart(2, '0'));
}

function Countdown({
  value: target,
  format = 'HH:mm:ss',
  onFinish,
  title,
  prefix,
  suffix,
  valueStyle,
  style,
  ...props
}: CountdownProps) {
  const [now, setNow] = useState(Date.now());
  const finishedRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= target && !finishedRef.current) {
        finishedRef.current = true;
        onFinish?.();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [target, onFinish]);

  const diff = Math.max(0, target - now);
  const display = formatCountdown(diff, format);

  return (
    <Statistic
      value={display}
      title={title}
      prefix={prefix}
      suffix={suffix}
      valueStyle={valueStyle}
      style={style}
      {...props}
    />
  );
}

Statistic.Countdown = Countdown;
